import { Permission } from '@weave/schema-gen-ts/dist/shared/waccess';
import { AiReceptionistOrg } from '@wetags/ai-receptionist';
import { useSupportedBookingSoftware, useTruelarkDependency } from '@frontend/api-ai-settings';
import { checkAggregateACL } from '@frontend/auth-helpers';
import { useAppScopeStore } from '@frontend/scope';
import { AIR_COHORT } from '../constants';
import { useAiReceptionistAccess } from './use-ai-receptionist-access';

export const useAiScheduleAccess = () => {
  const { selectedLocationIds, selectedOrgId } = useAppScopeStore();
  const { hasAiReceptionistAccess } = useAiReceptionistAccess();
  const [isAirSettingsEnabled, isAirSettingsEnabledLoading] = AiReceptionistOrg.useWetag('air-settings'); // wetag for new air-settings

  /**
   * Even though the ACL is named "AI_SETTINGS_WRITE", it is used to gate access to view and edit AI Receptionist settings.
   */
  const { aggregateValue: hasManagePermission } = checkAggregateACL(
    selectedLocationIds,
    Permission.AI_SETTINGS_WRITE,
    'all'
  );

  const airCohort = isAirSettingsEnabled ? AIR_COHORT.STEELTHREAD : AIR_COHORT.BRIDGELINE;

  const { meetsTruelarkDependency, isLoading: isTruelarkDependencyLoading } = useTruelarkDependency({
    locationIds: selectedLocationIds,
    orgId: selectedOrgId,
    enabled: airCohort === AIR_COHORT.BRIDGELINE && hasManagePermission,
  });

  const { hasSupportedBookingSoftware, isLoading: isBookingSoftwareLoading } = useSupportedBookingSoftware({
    locationIds: selectedLocationIds,
    orgId: selectedOrgId,
    isEnabled: meetsTruelarkDependency && hasManagePermission,
  });

  const canViewBridgelineScheduleSettings =
    airCohort === AIR_COHORT.BRIDGELINE &&
    hasAiReceptionistAccess &&
    hasManagePermission &&
    hasSupportedBookingSoftware;
  const stillOnboardingBridgeline = airCohort === AIR_COHORT.BRIDGELINE && !meetsTruelarkDependency;
  /**
   * This only determines whether Bridgeline-backed schedule settings should be enabled.
   * Visibility decisions should be based on `canViewBridgelineScheduleSettings`.
   */
  const canEditBridgelineScheduleSettings = canViewBridgelineScheduleSettings && !stillOnboardingBridgeline;

  return {
    airCohort,
    canViewBridgelineScheduleSettings,
    canEditBridgelineScheduleSettings,
    hasAiReceptionistAccess,
    isLoading: isTruelarkDependencyLoading || isBookingSoftwareLoading || isAirSettingsEnabledLoading,
  };
};
